import { z } from 'zod';

import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin-only session/auth audit trail (read model). Rows come from the shared
 * `audit_log` table filtered to the `auth.*` actions, enriched with the actor's
 * name/email and the geo-IP resolved at capture time. Backend-only data - never
 * surfaced to the audited user.
 */

export const SESSION_AUDIT_ACTIONS = [
  'auth.login',
  'auth.login.failed',
  'auth.logout',
  'auth.logout.inactivity',
  'auth.idle.warning',
  'auth.idle.extended',
  // A user signed one of their OTHER devices out (My profile → Where you're
  // signed in). Deliberately not `auth.logout.*`: the report pairs logouts
  // with logins, and this device's own session carries on.
  'auth.session.revoked',
] as const;
export const SessionAuditActionSchema = z.enum(SESSION_AUDIT_ACTIONS);
export type SessionAuditAction = z.infer<typeof SessionAuditActionSchema>;

export const SessionAuditGeoSchema = z.object({
  country: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
});
export type SessionAuditGeo = z.infer<typeof SessionAuditGeoSchema>;

export const SessionAuditListItemSchema = z.object({
  id: z.string(),
  action: SessionAuditActionSchema,
  performedAt: z.string().describe('ISO-8601 timestamp of the event'),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  userPhone: z.string().nullable(),
  /** For failed logins: the email / number that was typed (no matching user). */
  attemptedIdentifier: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  geo: SessionAuditGeoSchema.nullable(),
  /** Extra context, e.g. a failed-login reason. */
  reason: z.string().nullable(),
});
export type SessionAuditListItem = z.infer<typeof SessionAuditListItemSchema>;

export const SessionAuditListQuerySchema = PaginationQuerySchema.extend({
  action: SessionAuditActionSchema.optional(),
  userId: z.string().uuid().optional(),
  /** ISO datetime lower bound (inclusive). */
  from: z.string().optional(),
  /** ISO datetime upper bound (inclusive). */
  to: z.string().optional(),
});
export type SessionAuditListQuery = z.infer<typeof SessionAuditListQuerySchema>;

export const SessionAuditListResponseSchema = paginatedResponseSchema(SessionAuditListItemSchema);
export type SessionAuditListResponse = z.infer<typeof SessionAuditListResponseSchema>;
